import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, ArrowRight, MailCheck, ArrowLeft } from "lucide-react";
import { Logo } from "@/components/Logo";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { LangToggle } from "@/components/LangToggle";

export function AuthCard({ children }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-6">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-between"><Logo /><LangToggle /></div>
        {children}
      </motion.div>
    </div>
  );
}

export const inputCls = "mt-1.5 w-full rounded-xl border border-input bg-card px-4 py-3 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20";

export default function ForgotPassword() {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try { const { data } = await api.post("/auth/forgot-password", { email }); setSent(data.message); }
    catch (err) { setError(apiError(err)); } finally { setLoading(false); }
  };

  return (
    <AuthCard>
      {sent ? (
        <div data-testid="forgot-success">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-700"><MailCheck className="h-7 w-7" /></div>
          <h1 className="mt-6 font-display text-3xl font-extrabold tracking-tight">{t("checkInbox")}</h1>
          <p className="mt-2 text-muted-foreground">{sent}</p>
          <p className="mt-1 text-xs text-muted-foreground">Sent to <span className="font-mono font-semibold">{email}</span></p>
          <Link to="/login" data-testid="back-to-login-link" className="mt-8 inline-flex items-center gap-2 font-bold text-indigo-600 hover:underline"><ArrowLeft className="h-4 w-4" /> {t("backToLogin")}</Link>
        </div>
      ) : (
        <>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">{t("forgotTitle")}</h1>
          <p className="mt-2 text-muted-foreground">{t("forgotSub")}</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="text-sm font-semibold">{t("email")}</label>
              <input data-testid="forgot-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required placeholder="you@email.com" className={inputCls} />
            </div>
            {error && <p data-testid="forgot-error" className="text-sm font-medium text-destructive">{error}</p>}
            <button type="submit" disabled={loading} data-testid="forgot-submit-btn"
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 py-3.5 font-bold text-white transition-all hover:bg-indigo-700 active:scale-[0.99] disabled:opacity-60">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>{t("sendResetLink")} <ArrowRight className="h-4 w-4" /></>}
            </button>
          </form>
          <Link to="/login" data-testid="back-to-login-link" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-indigo-600 hover:underline"><ArrowLeft className="h-4 w-4" /> {t("backToLogin")}</Link>
        </>
      )}
    </AuthCard>
  );
}
